import { useCallback, useEffect, useState } from 'react';
import * as crmApi from '../utils/crmApi';

export function useAutomations() {
    const [automations, setAutomations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchAutomations = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await crmApi.getAutomations();
            setAutomations(Array.isArray(data.automations) ? data.automations : []);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAutomations();
    }, [fetchAutomations]);

    const create = useCallback(async (payload) => crmApi.createAutomation(payload), []);
    const update = useCallback(async (id, payload) => crmApi.updateAutomation(id, payload), []);
    const toggle = useCallback(async (id, enabled) => crmApi.toggleAutomation(id, enabled), []);

    return {
        automations,
        loading,
        error,
        refetch: fetchAutomations,
        create,
        update,
        toggle,
    };
}

export function useAutomationLogs(range = '7d') {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await crmApi.getAutomationLogs(range);
            setLogs(Array.isArray(data.logs) ? data.logs : []);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [range]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    return { logs, loading, error, refetch: fetchLogs };
}

export function useAutomationTest() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const runTest = useCallback(async (payload) => {
        setLoading(true);
        setError(null);
        try {
            const data = await crmApi.testAutomation(payload);
            return data;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return { runTest, loading, error };
}

export function useAutomationTemplates() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchTemplates = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await crmApi.getAutomationTemplates();
            setTemplates(Array.isArray(data.templates) ? data.templates : []);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates]);

    return {
        templates,
        loading,
        error,
        refetch: fetchTemplates,
        create: crmApi.createAutomationTemplate,
        update: crmApi.updateAutomationTemplate,
        remove: crmApi.deleteAutomationTemplate,
    };
}

export function useAutomationOutbox() {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchOutbox = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await crmApi.getAutomationOutbox();
            setMessages(Array.isArray(data.messages) ? data.messages : []);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOutbox();
    }, [fetchOutbox]);

    return {
        messages,
        loading,
        error,
        refetch: fetchOutbox,
        approve: crmApi.approveOutboxMessage,
        cancel: crmApi.cancelOutboxMessage,
        retry: crmApi.retryOutboxMessage,
        markSent: crmApi.markOutboxMessageSent,
    };
}

export function useAutomationSettings() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await crmApi.getAutomationSettings();
            setSettings(data);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const save = useCallback(async (payload) => {
        const data = await crmApi.saveAutomationSettings(payload);
        setSettings(data);
        return data;
    }, []);

    return { settings, loading, error, refetch: fetchSettings, save };
}

export function useAutomationPlaybooks() {
    const [playbooks, setPlaybooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchPlaybooks = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await crmApi.getAutomationPlaybooks();
            setPlaybooks(Array.isArray(data.playbooks) ? data.playbooks : []);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPlaybooks();
    }, [fetchPlaybooks]);

    const save = useCallback(async (id, payload) => {
        const data = await crmApi.updateAutomationPlaybook(id, payload);
        setPlaybooks((prev) => prev.map((item) => (item.id === data.id ? data : item)));
        return data;
    }, []);

    return { playbooks, loading, error, refetch: fetchPlaybooks, save };
}
